import { useState, useEffect } from 'react';
import {
  Users, 
  DollarSign, 
  TrendingUp, 
  Activity,
  CreditCard,
  Settings,
  LogOut,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit,
  Crown,
  Zap,
  Target,
  Gift,
  Server,
  Database,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  BarChart3
} from 'lucide-react';

// Admin panel MUST hit the backend directly - hardcode production URL
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1');

const API_BASE = import.meta.env.VITE_API_URL || (isLocalhost
  ? 'http://localhost:3001'
  : 'https://betmimic-production.up.railway.app');

const CLIENT_TOKEN = import.meta.env.VITE_BETMIMIC_CLIENT_TOKEN || 'betmimic-frontend-v1';

// Admin API helper
const adminApi = {
  token: null,
  
  setToken(token) {
    this.token = token;
    localStorage.setItem('admin_token', token);
  },
  
  getToken() {
    if (!this.token) {
      this.token = localStorage.getItem('admin_token');
    }
    return this.token;
  },
  
  clearToken() {
    this.token = null;
    localStorage.removeItem('admin_token');
  },
  
  async request(endpoint, options = {}) {
    const token = this.getToken();
    const url = `${API_BASE}/api/admin${endpoint}`;
    console.log(`🔑 Admin API request: ${options.method || 'GET'} ${url}`);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout
      
      const res = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'X-BetMimic-Client': CLIENT_TOKEN,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers
        }
      });
      
      clearTimeout(timeoutId);
      console.log(`🔑 Admin API response: ${res.status} ${res.statusText}`);
      
      if (res.status === 401) {
        this.clearToken();
        throw new Error('Unauthorized');
      }
      
      // Handle empty responses
      const text = await res.text();
      console.log(`🔑 Admin API response body length: ${text.length}`);
      
      if (!text) {
        if (!res.ok) throw new Error(`Request failed: ${res.status} ${res.statusText}`);
        return {};
      }
      
      const data = JSON.parse(text);
      if (!res.ok) throw new Error(data.error || 'Request failed');
      return data;
    } catch (err) {
      if (err.message === 'Unauthorized') throw err;
      if (err.name === 'AbortError') {
        console.error('Admin API timeout');
        throw new Error('Request timed out - server may be unavailable');
      }
      console.error('Admin API error:', err);
      throw new Error(err.message || 'Network error');
    }
  },
  
  get(endpoint) {
    return this.request(endpoint);
  },
  
  post(endpoint, body) {
    return this.request(endpoint, { method: 'POST', body: JSON.stringify(body) });
  },
  
  put(endpoint, body) {
    return this.request(endpoint, { method: 'PUT', body: JSON.stringify(body) });
  }
};

// Login Component
function AdminLogin({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const res = await adminApi.post('/login', { username, password });
      adminApi.setToken(res.token);
      onLogin();
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-red-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Settings className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
          <p className="text-dark-400 mt-1">BetMimic Administration</p>
        </div>
        
        <form onSubmit={handleSubmit} className="bg-dark-800 rounded-xl p-6 border border-dark-700 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-red-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-red-500"
              required
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Stat Card
function StatCard({ title, value, subtitle, icon: Icon, color = 'blue' }) {
  const colors = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    purple: 'bg-purple-500/20 text-purple-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    red: 'bg-red-500/20 text-red-400'
  };

  return (
    <div className="bg-dark-800 rounded-xl p-4 border border-dark-700">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-dark-400 text-sm">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {subtitle && <p className="text-dark-500 text-xs mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

// Dashboard Tab
function DashboardTab({ stats }) {
  if (!stats) return <div className="text-dark-400">Loading...</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-white">Dashboard Overview</h2>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total Users" value={stats.users?.total || 0} subtitle={`${stats.users?.today || 0} today`} icon={Users} color="blue" />
        <StatCard title="Verified Users" value={stats.users?.verified || 0} icon={CheckCircle} color="green" />
        <StatCard title="Active Subscriptions" value={stats.subscriptions?.active || 0} icon={Crown} color="purple" />
        <StatCard title="Total Revenue" value={`$${(stats.payments?.totalRevenue || 0).toFixed(2)}`} subtitle={`$${(stats.payments?.revenueThisMonth || 0).toFixed(2)} this month`} icon={DollarSign} color="green" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total Trades" value={stats.trades?.total || 0} subtitle={`${stats.trades?.today || 0} today`} icon={TrendingUp} color="blue" />
        <StatCard title="Open Positions" value={stats.positions?.open || 0} icon={Target} color="yellow" />
        <StatCard title="Copy Configs" value={stats.configs?.copyTrade || 0} icon={Target} color="purple" />
        <StatCard title="Whale Configs" value={stats.configs?.whaleVault || 0} icon={Crown} color="yellow" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard title="Total Referrals" value={stats.referrals?.total || 0} subtitle={`${stats.referrals?.paid || 0} paid`} icon={Gift} color="green" />
        <StatCard title="Referral Earnings" value={`$${(stats.referrals?.totalEarnings || 0).toFixed(2)}`} icon={DollarSign} color="purple" />
        <StatCard title="Arbitrage Configs" value={stats.configs?.arbitrage || 0} icon={Zap} color="blue" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-dark-800 rounded-xl p-4 border border-dark-700">
          <h3 className="text-lg font-semibold text-white mb-3">Subscriptions by Tier</h3>
          <div className="space-y-2">
            {Object.entries(stats.subscriptions?.byTier || {}).map(([tier, count]) => (
              <div key={tier} className="flex items-center justify-between">
                <span className="text-dark-300 capitalize">{tier}</span>
                <span className="text-white font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="bg-dark-800 rounded-xl p-4 border border-dark-700">
          <h3 className="text-lg font-semibold text-white mb-3">Trades by Status</h3>
          <div className="space-y-2">
            {Object.entries(stats.trades?.byStatus || {}).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className="text-dark-300 capitalize">{status}</span>
                <span className="text-white font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Users Tab
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/users?page=${page}&limit=20&search=${search}`);
      setUsers(res.users);
      setPagination(res.pagination);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Users ({pagination.total || 0})</h2>
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email or wallet..."
            className="px-3 py-1.5 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm w-64"
          />
          <button type="submit" className="px-3 py-1.5 bg-primary-500 rounded-lg text-white text-sm">
            <Search className="w-4 h-4" />
          </button>
        </form>
      </div>

      <div className="bg-dark-800 rounded-xl border border-dark-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-dark-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Tier</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Verified</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Created</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-700">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-dark-400">Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-dark-400">No users found</td></tr>
            ) : users.map(user => (
              <tr key={user._id} className="hover:bg-dark-700/50">
                <td className="px-4 py-3 text-sm text-white">{user.email}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    user.subscriptionTier === 'whale' ? 'bg-yellow-500/20 text-yellow-400' :
                    user.subscriptionTier === 'pro' ? 'bg-purple-500/20 text-purple-400' :
                    user.subscriptionTier === 'basic' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-dark-600 text-dark-400'
                  }`}>
                    {user.subscriptionTier || 'free'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {user.emailVerified ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-400" />
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    user.accountStatus === 'active' ? 'bg-green-500/20 text-green-400' :
                    user.accountStatus === 'suspended' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {user.accountStatus || 'active'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-dark-400">
                  {new Date(user.createdAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => setSelectedUser(user)}
                    className="p-1 hover:bg-dark-600 rounded"
                  >
                    <Eye className="w-4 h-4 text-dark-400" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-2 bg-dark-700 rounded-lg disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4 text-white" />
          </button>
          <span className="text-dark-400 text-sm">
            Page {page} of {pagination.pages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
            disabled={page === pagination.pages}
            className="p-2 bg-dark-700 rounded-lg disabled:opacity-50"
          >
            <ChevronRight className="w-4 h-4 text-white" />
          </button>
        </div>
      )}

      {selectedUser && (
        <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} onUpdate={fetchUsers} />
      )}
    </div>
  );
}

// User Detail Modal
function UserDetailModal({ user, onClose, onUpdate }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editTier, setEditTier] = useState(false);
  const [newTier, setNewTier] = useState(user.subscriptionTier || 'free');

  useEffect(() => {
    adminApi.get(`/users/${user._id}`).then(res => {
      setDetails(res);
      setLoading(false);
    });
  }, [user._id]);

  const updateSubscription = async () => {
    try {
      await adminApi.put(`/users/${user._id}/subscription`, {
        tier: newTier,
        isActive: newTier !== 'free',
        endDate: newTier !== 'free' ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) : null
      });
      setEditTier(false);
      onUpdate();
    } catch (err) {
      alert(err.message);
    }
  };

  const updateStatus = async (status) => {
    try {
      await adminApi.put(`/users/${user._id}/status`, { accountStatus: status });
      onUpdate();
      onClose();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-xl border border-dark-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-dark-700 flex items-center justify-between sticky top-0 bg-dark-800">
          <h3 className="text-lg font-semibold text-white">User Details</h3>
          <button onClick={onClose} className="text-dark-400 hover:text-white">✕</button>
        </div>
        
        {loading ? (
          <div className="p-8 text-center text-dark-400">Loading...</div>
        ) : (
          <div className="p-4 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-dark-400 text-sm">Email</p>
                <p className="text-white">{details.user.email}</p>
              </div>
              <div>
                <p className="text-dark-400 text-sm">User ID</p>
                <p className="text-white font-mono text-sm">{details.user._id}</p>
              </div>
              <div>
                <p className="text-dark-400 text-sm">Wallet</p>
                <p className="text-white font-mono text-sm">{details.user.wallet?.address || 'Not connected'}</p>
              </div>
              <div>
                <p className="text-dark-400 text-sm">Balance</p>
                <p className="text-white">${(details.user.wallet?.balance || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-dark-400 text-sm">Created</p>
                <p className="text-white">{new Date(details.user.createdAt).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-dark-400 text-sm">Last Login</p>
                <p className="text-white">{details.user.lastLoginAt ? new Date(details.user.lastLoginAt).toLocaleString() : 'Never'}</p>
              </div>
            </div>

            <div className="border-t border-dark-700 pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-dark-400 text-sm">Subscription</p>
                <button onClick={() => setEditTier(!editTier)} className="text-primary-400 text-sm">
                  {editTier ? 'Cancel' : 'Edit'}
                </button>
              </div>
              {editTier ? (
                <div className="flex gap-2">
                  <select
                    value={newTier}
                    onChange={(e) => setNewTier(e.target.value)}
                    className="flex-1 px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                  >
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="whale">Whale</option>
                  </select>
                  <button onClick={updateSubscription} className="px-4 py-2 bg-green-500 rounded-lg text-white">
                    Save
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded text-sm font-medium ${
                    details.subscription?.tier === 'whale' ? 'bg-yellow-500/20 text-yellow-400' :
                    details.subscription?.tier === 'pro' ? 'bg-purple-500/20 text-purple-400' :
                    details.subscription?.tier === 'basic' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-dark-600 text-dark-400'
                  }`}>
                    {details.subscription?.tier || 'free'}
                  </span>
                  {details.subscription?.endDate && (
                    <span className="text-dark-400 text-sm">
                      Expires: {new Date(details.subscription.endDate).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-dark-700 pt-4">
              <p className="text-dark-400 text-sm mb-2">Account Actions</p>
              <div className="flex gap-2">
                <button
                  onClick={() => updateStatus('active')}
                  className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-sm"
                >
                  Activate
                </button>
                <button
                  onClick={() => updateStatus('suspended')}
                  className="px-3 py-1.5 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm"
                >
                  Suspend
                </button>
                <button
                  onClick={() => updateStatus('banned')}
                  className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm"
                >
                  Ban
                </button>
              </div>
            </div>

            <div className="border-t border-dark-700 pt-4">
              <p className="text-dark-400 text-sm mb-2">Trades ({details.trades?.length || 0})</p>
              <div className="max-h-40 overflow-y-auto bg-dark-700/50 rounded-lg">
                {details.trades?.slice(0, 10).map(trade => (
                  <div key={trade._id} className="px-3 py-2 border-b border-dark-600 last:border-0 text-sm">
                    <span className={trade.side === 'BUY' ? 'text-green-400' : 'text-red-400'}>{trade.side}</span>
                    <span className="text-dark-400 ml-2">${trade.executedSize?.toFixed(2) || '0'}</span>
                    <span className="text-dark-500 ml-2">{new Date(trade.createdAt).toLocaleString()}</span>
                    <span className={`ml-2 ${trade.status === 'filled' ? 'text-green-400' : trade.status === 'failed' ? 'text-red-400' : 'text-yellow-400'}`}>
                      {trade.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-dark-700 pt-4">
              <p className="text-dark-400 text-sm mb-2">Payments ({details.payments?.length || 0})</p>
              <div className="max-h-40 overflow-y-auto bg-dark-700/50 rounded-lg">
                {details.payments?.map(payment => (
                  <div key={payment._id} className="px-3 py-2 border-b border-dark-600 last:border-0 text-sm">
                    <span className="text-white">${payment.amount}</span>
                    <span className="text-dark-400 ml-2">{payment.tier}</span>
                    <span className="text-dark-500 ml-2">{new Date(payment.createdAt).toLocaleString()}</span>
                    <span className={`ml-2 ${payment.status === 'confirmed' ? 'text-green-400' : 'text-yellow-400'}`}>
                      {payment.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Payments Tab
function PaymentsTab() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    adminApi.get(`/payments?page=${page}&limit=30`).then(res => {
      setPayments(res.payments);
      setPagination(res.pagination);
      setLoading(false);
    });
  }, [page]);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-white">Payments ({pagination.total || 0})</h2>

      <div className="bg-dark-800 rounded-xl border border-dark-700 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-dark-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">User</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Tier</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">TX Hash</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-700">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-dark-400">Loading...</td></tr>
            ) : payments.map(p => (
              <tr key={p._id} className="hover:bg-dark-700/50">
                <td className="px-4 py-3 text-sm text-white">{p.userId?.email || 'Unknown'}</td>
                <td className="px-4 py-3 text-sm text-green-400">${p.amount}</td>
                <td className="px-4 py-3 text-sm text-white capitalize">{p.tier}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs ${
                    p.status === 'confirmed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-dark-400 font-mono">
                  {p.transaction?.hash?.slice(0, 10)}...
                </td>
                <td className="px-4 py-3 text-sm text-dark-400">
                  {new Date(p.createdAt).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 bg-dark-700 rounded-lg disabled:opacity-50">
            <ChevronLeft className="w-4 h-4 text-white" />
          </button>
          <span className="text-dark-400 text-sm">Page {page} of {pagination.pages}</span>
          <button onClick={() => setPage(p => Math.min(pagination.pages, p + 1))} disabled={page === pagination.pages} className="p-2 bg-dark-700 rounded-lg disabled:opacity-50">
            <ChevronRight className="w-4 h-4 text-white" />
          </button>
        </div>
      )}
    </div>
  );
}

// Trades Tab
function TradesTab() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    adminApi.get(`/trades?page=${page}&limit=30`).then(res => {
      setTrades(res.trades);
      setPagination(res.pagination);
      setLoading(false);
    });
  }, [page]);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-white">Trades ({pagination.total || 0})</h2>

      <div className="bg-dark-800 rounded-xl border border-dark-700 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-dark-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">User</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Side</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Size</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Market</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-700">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-dark-400">Loading...</td></tr>
            ) : trades.map(t => (
              <tr key={t._id} className="hover:bg-dark-700/50">
                <td className="px-4 py-3 text-sm text-white">{t.userId?.email || 'Unknown'}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs ${t.side === 'BUY' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {t.side}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-white">${(t.executedSize || t.requestedSize || 0).toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs ${
                    t.status === 'filled' ? 'bg-green-500/20 text-green-400' :
                    t.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {t.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-dark-400 max-w-xs truncate">
                  {t.market?.question?.slice(0, 40) || 'N/A'}...
                </td>
                <td className="px-4 py-3 text-sm text-dark-400">
                  {new Date(t.createdAt).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 bg-dark-700 rounded-lg disabled:opacity-50">
            <ChevronLeft className="w-4 h-4 text-white" />
          </button>
          <span className="text-dark-400 text-sm">Page {page} of {pagination.pages}</span>
          <button onClick={() => setPage(p => Math.min(pagination.pages, p + 1))} disabled={page === pagination.pages} className="p-2 bg-dark-700 rounded-lg disabled:opacity-50">
            <ChevronRight className="w-4 h-4 text-white" />
          </button>
        </div>
      )}
    </div>
  );
}

// Referrals Tab
function ReferralsTab() {
  const [earnings, setEarnings] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    adminApi.get(`/referral-earnings?page=${page}&limit=30`).then(res => {
      setEarnings(res.earnings);
      setTotals(res.totals);
      setPagination(res.pagination);
      setLoading(false);
    });
  }, [page]);

  const markAsPaid = async (id) => {
    try {
      await adminApi.put(`/referral-earnings/${id}/status`, { status: 'paid' });
      setEarnings(prev => prev.map(e => e._id === id ? { ...e, status: 'paid' } : e));
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-white">Referral Earnings</h2>

      <div className="grid grid-cols-3 gap-4">
        <StatCard title="Confirmed" value={`$${(totals.confirmed?.total || 0).toFixed(2)}`} subtitle={`${totals.confirmed?.count || 0} transactions`} icon={CheckCircle} color="green" />
        <StatCard title="Pending" value={`$${(totals.pending?.total || 0).toFixed(2)}`} subtitle={`${totals.pending?.count || 0} transactions`} icon={Clock} color="yellow" />
        <StatCard title="Paid Out" value={`$${(totals.paid?.total || 0).toFixed(2)}`} subtitle={`${totals.paid?.count || 0} transactions`} icon={DollarSign} color="blue" />
      </div>

      <div className="bg-dark-800 rounded-xl border border-dark-700 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-dark-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Referrer</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Referred</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Level</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Commission</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-700">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-dark-400">Loading...</td></tr>
            ) : earnings.map(e => (
              <tr key={e._id} className="hover:bg-dark-700/50">
                <td className="px-4 py-3 text-sm text-white">{e.referrerId?.email || 'Unknown'}</td>
                <td className="px-4 py-3 text-sm text-dark-400">{e.referredUserId?.email || 'Unknown'}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs ${e.level === 1 ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>
                    L{e.level}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-green-400">${e.commissionAmount.toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs ${
                    e.status === 'paid' ? 'bg-blue-500/20 text-blue-400' :
                    e.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {e.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-dark-400">
                  {new Date(e.createdAt).toLocaleString()}
                </td>
                <td className="px-4 py-3">
                  {e.status === 'confirmed' && (
                    <button
                      onClick={() => markAsPaid(e._id)}
                      className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs hover:bg-blue-500/30"
                    >
                      Mark Paid
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 bg-dark-700 rounded-lg disabled:opacity-50">
            <ChevronLeft className="w-4 h-4 text-white" />
          </button>
          <span className="text-dark-400 text-sm">Page {page} of {pagination.pages}</span>
          <button onClick={() => setPage(p => Math.min(pagination.pages, p + 1))} disabled={page === pagination.pages} className="p-2 bg-dark-700 rounded-lg disabled:opacity-50">
            <ChevronRight className="w-4 h-4 text-white" />
          </button>
        </div>
      )}
    </div>
  );
}

// System Tab
function SystemTab() {
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSystem = () => {
    setLoading(true);
    adminApi.get('/system').then(res => {
      setSystem(res.system);
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchSystem();
  }, []);

  if (loading) return <div className="text-dark-400">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">System Status</h2>
        <button onClick={fetchSystem} className="p-2 bg-dark-700 rounded-lg hover:bg-dark-600">
          <RefreshCw className="w-4 h-4 text-white" />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          title="MongoDB" 
          value={system?.mongoStateText || 'Unknown'} 
          icon={Database} 
          color={system?.mongoState === 1 ? 'green' : 'red'} 
        />
        <StatCard 
          title="Uptime" 
          value={`${Math.floor((system?.uptime || 0) / 3600)}h ${Math.floor(((system?.uptime || 0) % 3600) / 60)}m`} 
          icon={Clock} 
          color="blue" 
        />
        <StatCard 
          title="Active Configs" 
          value={system?.copyTradeEngine?.activeConfigs || 0} 
          icon={Target} 
          color="purple" 
        />
        <StatCard 
          title="User Clients" 
          value={system?.copyTradeEngine?.userClients || 0} 
          icon={Users} 
          color="green" 
        />
      </div>

      <div className="bg-dark-800 rounded-xl p-4 border border-dark-700">
        <h3 className="text-lg font-semibold text-white mb-3">Memory Usage</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-dark-400 text-sm">Heap Used</p>
            <p className="text-white">{((system?.memoryUsage?.heapUsed || 0) / 1024 / 1024).toFixed(2)} MB</p>
          </div>
          <div>
            <p className="text-dark-400 text-sm">Heap Total</p>
            <p className="text-white">{((system?.memoryUsage?.heapTotal || 0) / 1024 / 1024).toFixed(2)} MB</p>
          </div>
          <div>
            <p className="text-dark-400 text-sm">RSS</p>
            <p className="text-white">{((system?.memoryUsage?.rss || 0) / 1024 / 1024).toFixed(2)} MB</p>
          </div>
        </div>
      </div>

      {system?.whaleModeEngine && (
        <div className="bg-dark-800 rounded-xl p-4 border border-dark-700">
          <h3 className="text-lg font-semibold text-white mb-3">Whale Mode Engine</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-dark-400 text-sm">Running</p>
              <p className={system.whaleModeEngine.running ? 'text-green-400' : 'text-red-400'}>
                {system.whaleModeEngine.running ? 'Yes' : 'No'}
              </p>
            </div>
            <div>
              <p className="text-dark-400 text-sm">Last Tick</p>
              <p className="text-white">{system.whaleModeEngine.lastTickIso || 'Never'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Main Admin Panel
export default function Admin() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if already logged in
    const token = adminApi.getToken();
    if (token) {
      adminApi.get('/verify').then(() => {
        setIsLoggedIn(true);
        fetchDashboard();
      }).catch(() => {
        adminApi.clearToken();
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await adminApi.get('/dashboard');
      setStats(res.stats);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    setIsLoggedIn(true);
    fetchDashboard();
  };

  const handleLogout = () => {
    adminApi.clearToken();
    setIsLoggedIn(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'trades', label: 'Trades', icon: TrendingUp },
    { id: 'referrals', label: 'Referrals', icon: Gift },
    { id: 'system', label: 'System', icon: Server }
  ];

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Header */}
      <div className="bg-dark-800 border-b border-dark-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">BetMimic Admin</h1>
              <p className="text-dark-500 text-xs">Administration Panel</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-1.5 bg-dark-700 hover:bg-dark-600 rounded-lg text-dark-300 text-sm"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-56 bg-dark-800 border-r border-dark-700 min-h-[calc(100vh-73px)]">
          <nav className="p-4 space-y-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'bg-red-500/20 text-red-400'
                    : 'text-dark-400 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          {activeTab === 'dashboard' && <DashboardTab stats={stats} />}
          {activeTab === 'users' && <UsersTab />}
          {activeTab === 'payments' && <PaymentsTab />}
          {activeTab === 'trades' && <TradesTab />}
          {activeTab === 'referrals' && <ReferralsTab />}
          {activeTab === 'system' && <SystemTab />}
        </div>
      </div>
    </div>
  );
}
